import type React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * One folding section of a form, with a tag saying how much is inside.
 *
 * Lives on its own rather than inside the one-day form because the per-day
 * cards of a multi-day operation ask the same questions and were laying them
 * out differently — plain headings, all open at once, in a card that is
 * already one of seven on screen. Shared, the two cannot drift again.
 */
export function FieldGroup({
  title, caption, tone = 'default', defaultExpanded = false, count = 0, tour, children,
}: {
  title: string;
  /** Names the group for the help tour — see GuidedTour. */
  tour?: string;
  caption?: string;
  tone?: 'default' | 'warning';
  /** Open from the start, for a group that already has something in it. */
  defaultExpanded?: boolean;
  /**
   * How much this group holds, shown as a tag beside its heading.
   *
   * Every one of these folds shut, so a closed panel said nothing about
   * whether anything was inside it — except "How this one travels", which
   * had a tag of its own and so looked like the only section that could be
   * filled in. Zero shows nothing: a tag reading "0" is a row of noise on a
   * form where most groups are empty most of the time.
   */
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      data-tour={tour}
      variant="outlined"
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        borderColor: tone === 'warning' ? 'warning.light' : 'divider',
        bgcolor: 'transparent',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography
              variant="overline"
              sx={{ flex: 1, lineHeight: 1.6, letterSpacing: '0.08em' }}
              color={tone === 'warning' ? 'warning.main' : 'text.secondary'}
            >
              {title}
            </Typography>
            {count > 0 && <Chip size="small" variant="outlined" label={count} />}
          </Stack>
          {caption && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {caption}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );
}
